import '../../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Select from '../../../components/ui/Select';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';

import Google from '../images/google-color-svgrepo-com.svg';
import Airbnb from '../images/airbnb-color-svgrepo-com.svg';
import Amazon from '../images/amazon-color-svgrepo-com.svg';
import Apple from '../images/apple-svgrepo-com.svg';
import Meta from '../images/meta-logo-facebook-svgrepo-com.svg';
import Microsoft from '../images/microsoft-svgrepo-com.svg';
import Netflix from '../images/netflix-2-logo-svgrepo-com.svg';
import Salesforce from '../images/salesforce-2-logo-svgrepo-com.svg';
import Spotify from '../images/spotify-color-svgrepo-com.svg';
import Stripe from '../images/stripe-svgrepo-com.svg';
import Tesla from '../images/tesla-9-logo-svgrepo-com.svg';
import Uber from '../images/uber-svgrepo-com.svg';

const RoleTargetingSection = ({
  embedded = false,
  selectedIndustry,
  onIndustryChange,
  selectedCompanyType,
  onCompanyTypeChange,
  selectedRole,
  onRoleChange,
  selectedCompaniesAttr = [],
  onCompaniesChange,
  className = '',
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState(selectedCompaniesAttr || []);

  // Sync internal state with external prop changes
  useEffect(() => {
    setSelectedCompanies(selectedCompaniesAttr || []);
  }, [selectedCompaniesAttr]);

  const industries = [
    { value: 'technology', label: 'Technology', description: 'Software, AI, Hardware, SaaS' },
    { value: 'finance', label: 'Finance & Banking', description: 'Investment, Insurance, Fintech' },
    { value: 'healthcare', label: 'Healthcare', description: 'Medical, Pharma, Biotech' },
    { value: 'consulting', label: 'Consulting', description: 'Strategy, Management, Operations' },
    { value: 'marketing', label: 'Marketing & Advertising', description: 'Digital, Brand, Growth' },
    { value: 'retail', label: 'Retail & E-commerce', description: 'Consumer goods, Online retail' },
    { value: 'manufacturing', label: 'Manufacturing', description: 'Industrial, Automotive, Aerospace' },
    { value: 'education', label: 'Education', description: 'EdTech, Universities, Training' },
    { value: 'energy', label: 'Energy & Utilities', description: 'Oil & Gas, Renewable energy, Power' },
    { value: 'telecom', label: 'Telecommunications', description: 'Wireless, Internet, Networking' },
    { value: 'media', label: 'Media & Entertainment', description: 'TV, Film, Music, Publishing' },
    { value: 'transportation', label: 'Transportation & Logistics', description: 'Shipping, Supply chain, Aviation' },
    { value: 'real_estate', label: 'Real Estate', description: 'Commercial, Residential, Property management' },
    { value: 'hospitality', label: 'Hospitality & Tourism', description: 'Hotels, Restaurants, Travel' },
    { value: 'construction', label: 'Construction', description: 'Building, Engineering, Architecture' },
    { value: 'agriculture', label: 'Agriculture', description: 'Farming, Food production, AgTech' },
    { value: 'government', label: 'Government', description: 'Public sector, Policy, Administration' },
    { value: 'non_profit', label: 'Non-Profit', description: 'Charity, Social services, Advocacy' },
    { value: 'legal', label: 'Legal Services', description: 'Law firms, Corporate law, Litigation' },
    { value: 'pharmaceutical', label: 'Pharmaceuticals', description: 'Drug development, Clinical research' },
    { value: 'automotive', label: 'Automotive', description: 'Vehicle manufacturing, Parts, Sales' },
    { value: 'aerospace', label: 'Aerospace & Defense', description: 'Aircraft, Space, Military' },
    { value: 'fashion', label: 'Fashion & Apparel', description: 'Clothing, Accessories, Retail' },
    { value: 'sports', label: 'Sports & Fitness', description: 'Athletics, Equipment, Wellness' },
    { value: 'gaming', label: 'Gaming', description: 'Video games, Esports, Development' },
  ];

  const companyTypes = [
    { value: 'startup', label: 'Startup (1-50 employees)', description: 'Fast-paced, innovative environment' },
    { value: 'scale_up', label: 'Scale-up (51-500 employees)', description: 'Growing company, structured processes' },
    { value: 'enterprise', label: 'Enterprise (500+ employees)', description: 'Large corporation, formal procedures' },
    { value: 'unicorn', label: 'Unicorn ($1B+ valuation)', description: 'High-growth, competitive culture' },
    { value: 'public', label: 'Public Company', description: 'Established, regulated environment' },
    { value: 'non_profit', label: 'Non-Profit', description: 'Mission-driven, social impact focus' },
    { value: 'government', label: 'Government Agency', description: 'Policy, administration, regulated work' },
    { value: 'freelance', label: 'Freelance / Independent', description: 'Contract-based, flexible structure' },
  ];

  const roles = [
    { value: 'software_engineer', label: 'Software Engineer', description: 'Full-stack, Frontend, Backend' },
    { value: 'product_manager', label: 'Product Manager', description: 'Strategy, Roadmap, User Experience' },
    { value: 'data_scientist', label: 'Data Scientist', description: 'Analytics, ML, Statistical modeling' },
    { value: 'designer', label: 'UX/UI Designer', description: 'User research, Visual design, Prototyping' },
    { value: 'marketing_manager', label: 'Marketing Manager', description: 'Growth, Brand, Digital marketing' },
    { value: 'sales_rep', label: 'Sales Representative', description: 'B2B, B2C, Account management' },
    { value: 'consultant', label: 'Management Consultant', description: 'Strategy, Operations, Transformation' },
    { value: 'analyst', label: 'Business Analyst', description: 'Requirements, Process improvement' },
    { value: 'devops_engineer', label: 'DevOps Engineer', description: 'CI/CD, Infrastructure, Automation' },
    { value: 'qa_engineer', label: 'QA Engineer', description: 'Testing, Quality assurance, Automation' },
    { value: 'project_manager', label: 'Project Manager', description: 'Planning, Execution, Delivery' },
    { value: 'cto', label: 'CTO', description: 'Technical strategy, Leadership, Innovation' },
    { value: 'ceo', label: 'CEO', description: 'Executive leadership, Strategy, Vision' },
    { value: 'cfo', label: 'CFO', description: 'Financial management, Planning, Analysis' },
    { value: 'cmo', label: 'CMO', description: 'Marketing strategy, Brand management' },
    { value: 'hr_manager', label: 'HR Manager', description: 'Talent acquisition, Employee relations' },
    { value: 'recruiter', label: 'Recruiter', description: 'Talent sourcing, Hiring, Interviews' },
    { value: 'accountant', label: 'Accountant', description: 'Financial reporting, Tax, Audit' },
    { value: 'financial_analyst', label: 'Financial Analyst', description: 'Budgeting, Forecasting, Analysis' },
    { value: 'operations_manager', label: 'Operations Manager', description: 'Process optimization, Efficiency' },
    { value: 'supply_chain_manager', label: 'Supply Chain Manager', description: 'Logistics, Procurement, Distribution' },
    { value: 'customer_support', label: 'Customer Support', description: 'Service, Troubleshooting, Retention' },
    { value: 'content_writer', label: 'Content Writer', description: 'Copywriting, Blogging, SEO' },
    { value: 'social_media_manager', label: 'Social Media Manager', description: 'Content, Engagement, Strategy' },
    { value: 'legal_counsel', label: 'Legal Counsel', description: 'Compliance, Contracts, Advisory' },
    { value: 'research_scientist', label: 'Research Scientist', description: 'R&D, Experiments, Analysis' },
    { value: 'network_engineer', label: 'Network Engineer', description: 'Infrastructure, Security, Maintenance' },
    { value: 'security_analyst', label: 'Security Analyst', description: 'Cybersecurity, Threat detection' },
    { value: 'data_engineer', label: 'Data Engineer', description: 'ETL, Pipelines, Data warehousing' },
    { value: 'machine_learning_engineer', label: 'Machine Learning Engineer', description: 'AI models, Algorithms, Deployment' },
  ];

  const popularCompanies = [
    { id: 'google', name: 'Google', logo: Google },
    { id: 'microsoft', name: 'Microsoft', logo: Microsoft },
    { id: 'apple', name: 'Apple', logo: Apple },
    { id: 'amazon', name: 'Amazon', logo: Amazon },
    { id: 'meta', name: 'Meta', logo: Meta },
    { id: 'tesla', name: 'Tesla', logo: Tesla },
    { id: 'netflix', name: 'Netflix', logo: Netflix },
    { id: 'spotify', name: 'Spotify', logo: Spotify },
    { id: 'airbnb', name: 'Airbnb', logo: Airbnb },
    { id: 'uber', name: 'Uber', logo: Uber },
    { id: 'stripe', name: 'Stripe', logo: Stripe },
    { id: 'salesforce', name: 'Salesforce', logo: Salesforce },
  ];

  const handleImageError = (e) => {
    e.target.src =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCA0QzI5LjUyMjggNCAzNCA4LjQ3NzE1IDM0IDE0QzM0IDE5LjUyMjggMjkuNTIyOCAyNCAyNCAyNEMxOC40NzcyIDI0IDE0IDE5LjUyMjggMTQgMTRDMTQgOC40NzcxNSAxOC40NzcyIDQgMjQgNFoiIGZpbGw9IiNENkQ4REIiLz4KPHBhdGggZD0iTTM5LjQ5MDMgNDJDNzIuODc4NyA0MiA0MiA3Mi44Nzg3IDQyIDM5LjQ5MDNDNDIgMjkuMDIyOSAzMy4yMjg4IDE2IDI0IDE2QzE0Ljc3MTIgMTYgNiAyOS4wMjI5IDYgMzkuNDkwM0M2IDcyLjg3ODcgMzUuMTIxMyA0MiAzOS40OTAzIDQyWiIgZmlsbD0iI0Q2RDhEQiIvPgo8L3N2Zz4K';
  };

  const toggleCompanySelection = (companyId) => {
    let updatedCompanies;

    if (selectedCompanies.includes(companyId)) {
      updatedCompanies = selectedCompanies.filter((id) => id !== companyId);
    } else {
      updatedCompanies = [...selectedCompanies, companyId];
    }

    setSelectedCompanies(updatedCompanies);

    if (onCompaniesChange) {
      onCompaniesChange(updatedCompanies);
    }
  };

  const isCompanySelected = (companyId) => selectedCompanies.includes(companyId);

  const panelBase =
  "relative isolate overflow-visible rounded-3xl border border-white/10 bg-[#0b0f16]/60 backdrop-blur-xl p-5 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_60px_-40px_rgba(56,189,248,0.20)]";
  
  return (
    <div className={`transition-all duration-200 ease-smooth space-y-6 ${className}`}>
      {!embedded && (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_0_18px_rgba(56,189,248,0.20)]">
              <img src="/bulleseye_czdi2g.png" alt="target" className="w-9 h-9" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Target Your Practice</h2>
          </div>
          <p className="text-white/60">
            Customize the interview experience for your specific career goals.
          </p>
        </div>
      )}

      {/* Industry / Role / Company Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`${panelBase} md:col-span-2 z-[600]`}>
          <div className="pointer-events-none absolute inset-0 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_55%)]" />
          </div>
      
          <div className="relative z-[1]">
            <Select
              label="Company Type"
              description="Choose the type of company you're targeting"
              options={companyTypes}
              value={selectedCompanyType}
              onChange={onCompanyTypeChange}
              placeholder="Select company type"
              className="w-full"
            />
          </div>
        </div>
        
        <div className={`${panelBase} z-[500]`}>
          {/* decorative bg clipped, not the dropdown */}
          <div className="pointer-events-none absolute inset-0 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_55%)]" />
          </div>
      
          {/* content above */}
          <div className="relative z-[1]">
            <Select
              label="Industry Focus"
              description="Choose the industry you're targeting"
              options={industries}
              value={selectedIndustry}
              onChange={onIndustryChange}
              placeholder="Select an industry"
              searchable
              className="w-full"
            />
          </div>
        </div>
      
        <div className={`${panelBase} z-[400]`}>
          <div className="pointer-events-none absolute inset-0 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_55%)]" />
          </div>
      
          <div className="relative z-[1]">
            <Select
              label="Target Role"
              description="Specify the role you're preparing for"
              options={roles}
              value={selectedRole}
              onChange={onRoleChange}
              placeholder="Select a role"
              searchable
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Companies */}
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/15 via-violet-500/10 to-emerald-500/10 backdrop-blur-xl shadow-[0_0_22px_rgba(56,189,248,0.22)]">
            <Icon name="Building" size={16} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Popular Companies</h3>
            <p className="text-sm text-white/60">
              Select companies to tailor question style, expectations, and examples.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr">
          {popularCompanies.map((company) => {
            const isSelected = isCompanySelected(company.id);

            return (
              <motion.button
                key={company.id}
                type="button"
                onClick={() => toggleCompanySelection(company.id)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.99 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                className="group relative text-left"
              >
                <SpotlightCard
                  className="custom-spotlight-card h-full rounded-3xl overflow-hidden"
                  spotlightColor={isSelected ? 'rgba(56, 189, 248, 0.26)' : 'rgba(255,255,255,0.06)'}
                >
                  <div
                    className={`relative h-full rounded-3xl border p-4 flex flex-col items-center justify-center transition-all duration-300 backdrop-blur-xl overflow-hidden
                      ${
                        isSelected
                          ? 'border-white/18 bg-gradient-to-br from-sky-500/18 via-violet-500/10 to-emerald-500/10 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_18px_60px_-42px_rgba(56,189,248,0.45)]'
                          : 'border-white/10 bg-[#0b0f16]/60 hover:border-white/16 hover:bg-white/[0.04]'
                      }
                    `}
                  >
                    <div className="absolute inset-0 opacity-60 pointer-events-none [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.10),transparent_60%)]" />

                    <div
                      className={`relative w-16 h-16 mb-3 rounded-2xl flex items-center justify-center overflow-hidden border transition-transform duration-300 group-hover:scale-[1.03] ${
                        isSelected
                          ? 'bg-white/80 border-white/20'
                          : 'bg-white/70 border-white/15'
                      }`}
                    >
                      <img
                        src={company.logo}
                        alt={`${company.name} logo`}
                        className="w-12 h-12 object-contain"
                        onError={handleImageError}
                      />
                    </div>

                    <span
                      className={`relative text-xs font-medium text-center transition-colors ${
                        isSelected ? 'text-white' : 'text-white/80 group-hover:text-white'
                      }`}
                    >
                      {company.name}
                    </span>

                    {isSelected && (
                      <div className="absolute top-3 right-3 w-7 h-7 rounded-2xl bg-emerald-400/12 border border-emerald-300/20 backdrop-blur-xl flex items-center justify-center shadow-[0_0_16px_rgba(16,185,129,0.50)]">
                        <Icon name="Check" size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                </SpotlightCard>
              </motion.button>
            );
          })}
        </div>

        {selectedCompanies.length > 0 && (
          <div className="relative overflow-hidden rounded-3xl p-5 border border-white/10 bg-[#0b0f16]/60 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_60px_-40px_rgba(16,185,129,0.25)]">
            <div className="absolute inset-0 opacity-70 pointer-events-none [background:radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.18),transparent_55%)]" />
            <div className="relative flex items-center gap-2">
              <Icon name="Check" size={16} className="text-white" />
              <span className="text-sm text-white">
                Selected: {selectedCompanies.length} company{selectedCompanies.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="relative mt-3 flex flex-wrap gap-2">
              {selectedCompanies.map((companyId) => {
                const company = popularCompanies.find((c) => c.id === companyId);
                return company ? (
                  <span
                    key={companyId}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-white/[0.04] text-white/85 border border-white/10"
                  >
                    {company.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleTargetingSection;
